import apiClient from './apiClient';
import type {
  HistoryResponse,
  DailySummaryDTO,
  DailyOperationsDTO,
  DailyQueueDTO
} from '../types';

/**
 * Service para interagir com a API de Histórico
 *
 * Endpoints:
 * - GET /history - Todos os dados históricos
 * - GET /history/summary - Histórico de totais (Schools, Students, Teachers, Classes)
 * - GET /history/operations - Histórico de operações (sucesso/falha)
 * - GET /history/dlq - Histórico de DLQ
 * - GET /history/scheduled - Histórico de mensagens agendadas
 */
export const historyService = {
  /**
   * Obtém todos os dados históricos em uma única chamada
   * @param startDate Data inicial (default: 15 dias atrás)
   * @param endDate Data final (default: hoje)
   */
  async getAll(startDate?: Date, endDate?: Date): Promise<HistoryResponse> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate.toISOString());
    if (endDate) params.append('endDate', endDate.toISOString());

    const url = `/history${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await apiClient.get<HistoryResponse>(url);
    return response.data;
  },

  /**
   * Obtém histórico de totais de entidades
   * @param startDate Data inicial (default: 15 dias atrás)
   * @param endDate Data final (default: hoje)
   */
  async getSummary(startDate?: Date, endDate?: Date): Promise<DailySummaryDTO[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate.toISOString());
    if (endDate) params.append('endDate', endDate.toISOString());

    const url = `/history/summary${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await apiClient.get<DailySummaryDTO[]>(url);
    return response.data;
  },

  /**
   * Obtém histórico de operações (sucesso/falha por dia)
   * @param startDate Data inicial (default: 15 dias atrás)
   * @param endDate Data final (default: hoje)
   */
  async getOperations(startDate?: Date, endDate?: Date): Promise<DailyOperationsDTO[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate.toISOString());
    if (endDate) params.append('endDate', endDate.toISOString());

    const url = `/history/operations${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await apiClient.get<DailyOperationsDTO[]>(url);
    return response.data;
  },

  /**
   * Obtém histórico de DLQ por dia
   * @param startDate Data inicial (default: 15 dias atrás)
   * @param endDate Data final (default: hoje)
   */
  async getDlq(startDate?: Date, endDate?: Date): Promise<DailyQueueDTO[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate.toISOString());
    if (endDate) params.append('endDate', endDate.toISOString());

    const url = `/history/dlq${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await apiClient.get<DailyQueueDTO[]>(url);
    return response.data;
  },

  /**
   * Obtém histórico de mensagens agendadas por dia
   * @param startDate Data inicial (default: 15 dias atrás)
   * @param endDate Data final (default: hoje)
   */
  async getScheduled(startDate?: Date, endDate?: Date): Promise<DailyQueueDTO[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate.toISOString());
    if (endDate) params.append('endDate', endDate.toISOString());

    const url = `/history/scheduled${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await apiClient.get<DailyQueueDTO[]>(url);
    return response.data;
  },

  /**
   * Formata data para exibição no formato brasileiro
   * Usa UTC para evitar que a data mude por causa do timezone
   */
  formatDate(dateString: string | Date): string {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  },

  /**
   * Formata data para exibição curta (DD/MM)
   * Usa UTC para evitar que a data mude por causa do timezone
   */
  formatDateShort(dateString: string | Date): string {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: 'UTC' });
  },

  /**
   * Formata número com separador de milhares
   */
  formatNumber(value: number): string {
    return value.toLocaleString('pt-BR');
  },

  /**
   * Calcula datas para filtro de ano/meses
   * @param year Ano selecionado
   * @param months Array de meses selecionados (1-12)
   */
  calculateDateRange(year: number, months: number[]): { startDate: Date; endDate: Date } {
    if (months.length === 0) {
      // Ano inteiro
      return {
        startDate: new Date(year, 0, 1),
        endDate: new Date(year, 11, 31)
      };
    }

    const sortedMonths = [...months].sort((a, b) => a - b);
    const firstMonth = sortedMonths[0];
    const lastMonth = sortedMonths[sortedMonths.length - 1];

    return {
      startDate: new Date(year, firstMonth - 1, 1),
      endDate: new Date(year, lastMonth, 0) // Último dia do mês
    };
  },

  /**
   * Gera lista de anos disponíveis (do ano atual até 2024)
   */
  getAvailableYears(): number[] {
    const currentYear = new Date().getFullYear();
    const years: number[] = [];
    for (let y = currentYear; y >= 2024; y--) {
      years.push(y);
    }
    return years;
  },

  /**
   * Lista de meses para filtro
   */
  getMonths(): { value: number; label: string }[] {
    return [
      { value: 1, label: 'Janeiro' },
      { value: 2, label: 'Fevereiro' },
      { value: 3, label: 'Março' },
      { value: 4, label: 'Abril' },
      { value: 5, label: 'Maio' },
      { value: 6, label: 'Junho' },
      { value: 7, label: 'Julho' },
      { value: 8, label: 'Agosto' },
      { value: 9, label: 'Setembro' },
      { value: 10, label: 'Outubro' },
      { value: 11, label: 'Novembro' },
      { value: 12, label: 'Dezembro' }
    ];
  }
};
